"use client";

import { useFinanceSync } from "@/lib/finance/finance-data-context";
import { timeAgo } from "@/lib/format";

/**
 * Refresh control — the finance app is served cache-first and never
 * auto-refetches, so this is the one place data is pulled from the store.
 * Refresh is an incremental pull (manifest diff → only changed bundles).
 * When a newer app build is deployed the button becomes "Update app" instead:
 * pulling new app assets is a reload-the-shell operation, kept separate from
 * data refresh so a data pull never pays a reload.
 */
export function RefreshBar() {
  const { refreshing, lastRefreshed, updateAvailable, appUpdateAvailable, refresh, update } = useFinanceSync();

  const nudge = appUpdateAvailable || updateAvailable;
  return (
    <div className="flex items-center gap-2">
      <span className="hidden text-[11px] text-ink-3 sm:inline">
        {appUpdateAvailable
          ? "New app version"
          : updateAvailable
            ? "New data available"
            : lastRefreshed
              ? `Updated ${timeAgo(lastRefreshed)}`
              : "Cached"}
      </span>
      <button
        onClick={appUpdateAvailable ? update : refresh}
        disabled={refreshing}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${
          nudge
            ? "border-transparent bg-accent text-white hover:opacity-90"
            : "border-edge text-ink-2 hover:bg-surface-3"
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M21 12a9 9 0 11-2.64-6.36M21 3v6h-6" />
        </svg>
        {refreshing ? "Refreshing…" : appUpdateAvailable ? "Update app" : "Refresh"}
      </button>
    </div>
  );
}
